import { TabControl } from "@components/ui/TabControl"
import { tv } from "@utils/tw"
import { defineBlock, EditableText, InnerBlocks, SingleContentBlock, useInnerBlocks } from "eddev/blocks"
import { useAppData } from "eddev/hooks"
import { AnimatePresence, motion } from "motion/react"
import { createContext, ReactNode, useContext, useState } from "react"

export const meta: BlockMeta = {
  title: "Donation Campaign",
  category: "embeds",
  description: "A large section to drive quick donations.",
  keywords: ["donate", "donation"],
  tags: ["#subpage-layout"],
  flags: {
    fullWidth: true,
    marginRule: "large-card",
  },
}

type DonateCampaignContextType = {
  amount: number | "custom" | undefined
  setAmount: (amount: number | "custom" | undefined) => void
  customAmount?: number
  setCustomAmount: (amount: number) => void
  ferveHash?: string
  working: boolean
  setWorking: (working: boolean) => void
  ferveItemHash?: string
  setFerveItemHash: (hash?: string) => void
  fervePriceTypeHash?: string
  setFervePriceTypeHash: (hash?: string) => void
}

const DonateCampaignContext = createContext<DonateCampaignContextType | undefined>(undefined)

export const useDonateCampaignContext = () => {
  const context = useContext(DonateCampaignContext)
  if (context === undefined) {
    throw new Error("useDonateCampaignContext must be used within a DonateCampaignProvider")
  }
  return context
}

export default defineBlock("tickets/donate-campaign", (props) => {
  const [amount, setAmount] = useState<number | "custom" | undefined>(undefined)
  const [customAmount, setCustomAmount] = useState<number>(0)
  const [working, setWorking] = useState(false)
  const [ferveItemHash, setFerveItemHash] = useState<string | undefined>(undefined)
  const [fervePriceTypeHash, setFervePriceTypeHash] = useState<string | undefined>(undefined)
  let donationHash = useAppData((data) => data.sffSettings?.donations?.ferveDonationsHash || undefined)

  if (props.ferveHash) {
    donationHash = props.ferveHash
  }

  const [activeTab, setActiveTab] = useState(0)
  let contents: ReactNode

  if (env.admin) {
    contents = <InnerBlocks allowedBlocks={["tickets/donate-campaign-page"]} />
  } else {
    const blocks = useInnerBlocks()
    const tab = blocks[activeTab]
    contents = (
      <>
        <div className="flex items-center justify-center py-6">
          {blocks.length > 0 && (
            <TabControl
              value={String(activeTab)}
              onValueChange={(tab) => setActiveTab(parseInt(tab))}
              items={blocks.map((block, i) => ({
                value: String(i),
                label: (block.inline?.tabTitle as string) || "",
              }))}
              variant="brand"
            />
          )}
        </div>
        <AnimatePresence mode="wait">
          {tab && (
            <motion.div
              key={activeTab}
              variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              <SingleContentBlock block={tab} />
            </motion.div>
          )}
        </AnimatePresence>
      </>
    )
  }

  return (
    <DonateCampaignContext.Provider
      value={{
        amount,
        setAmount,
        customAmount,
        setCustomAmount,
        ferveHash: donationHash,
        working,
        setWorking,
        ferveItemHash,
        setFerveItemHash,
        fervePriceTypeHash,
        setFervePriceTypeHash,
      }}
    >
      <div>
        <div className="md:grid-auto bg-orange">
          <div className="w-grid mx-auto md:w-auto md:col-span-10 md:col-start-2">{contents}</div>
        </div>
      </div>
    </DonateCampaignContext.Provider>
  )
})
